/**
 * États du pipeline de déploiement et bornes de progression associées.
 *
 * Référence : cahier des charges §3.3 (tableau des étapes) et §4.2 (machine à états).
 * Ce module ne dépend de rien : il est consommé tel quel par le builder,
 * par l'API et par le front.
 */
/** Liste ordonnée de tous les états possibles d'un déploiement. */
export const DEPLOYMENT_STATES = [
    'QUEUED',
    'TRANSFORMING',
    'AWAITING_REVIEW',
    'SCAFFOLDING',
    'BUILDING',
    'PUSHING',
    'DEPLOYING',
    'HEALTHCHECK',
    'DONE',
    'FAILED',
    'CANCELLED',
];
/**
 * Nature du job (§4.2).
 *
 * - `DEPLOY` : déploiement initial ou mise à jour, pipeline complet.
 * - `ROLLBACK` : restauration d'une version antérieure. L'image existe déjà
 *   dans le registre, il n'y a donc rien à transformer ni à construire — le job
 *   démarre directement à l'étape de déploiement.
 */
export const DEPLOYMENT_TYPES = ['DEPLOY', 'ROLLBACK'];
/**
 * Étapes réellement exécutées par le builder, dans l'ordre nominal.
 *
 * `QUEUED` n'en fait pas partie (le job attend), les états terminaux non plus,
 * et `AWAITING_REVIEW` pas davantage : le builder n'y travaille pas, il y a rendu
 * la main. Lui donner un segment de barre reviendrait à annoncer une septième
 * étape technique là où il n'y a qu'une question posée à l'utilisateur.
 */
export const PIPELINE_STEPS = [
    'TRANSFORMING',
    'SCAFFOLDING',
    'BUILDING',
    'PUSHING',
    'DEPLOYING',
    'HEALTHCHECK',
];
/** Étapes d'un rollback : ni transformation, ni construction, ni publication. */
export const ROLLBACK_STEPS = ['DEPLOYING', 'HEALTHCHECK'];
/**
 * Étapes attendues pour un type de job donné. La barre de progression du front
 * s'appuie dessus : un rollback n'affiche que deux segments, et non six dont
 * quatre resteraient éternellement vides.
 */
export function pipelineStepsFor(type) {
    return type === 'ROLLBACK' ? ROLLBACK_STEPS : PIPELINE_STEPS;
}
/** États depuis lesquels aucune transition n'est plus possible. */
export const TERMINAL_STATES = ['DONE', 'FAILED', 'CANCELLED'];
/**
 * Bornes de progression globale (en pourcentage) par état, conformément au
 * tableau du §3.3. La progression affichée à l'utilisateur interpole entre
 * `start` et `end` pendant toute la durée de l'étape.
 */
export const PROGRESS_RANGES = {
    QUEUED: { start: 0, end: 5 },
    TRANSFORMING: { start: 5, end: 25 },
    /**
     * L'attente d'une décision n'avance pas : la barre reste exactement là où la
     * transformation l'a laissée. Une plage propre laisserait croire à un travail
     * en cours, alors que rien ne progressera avant la réponse de l'utilisateur.
     */
    AWAITING_REVIEW: { start: 25, end: 25 },
    SCAFFOLDING: { start: 25, end: 35 },
    BUILDING: { start: 35, end: 70 },
    PUSHING: { start: 70, end: 80 },
    DEPLOYING: { start: 80, end: 97 },
    HEALTHCHECK: { start: 97, end: 100 },
    DONE: { start: 100, end: 100 },
    /**
     * Un échec et une annulation n'ont **pas** de progression propre : ils
     * conservent celle de l'étape où le pipeline s'est arrêté. La plage vaut donc
     * zéro, et `terminalProgress` décide de ce qui est affiché.
     *
     * Elle valait auparavant 100 %, ce qui produisait l'affichage le plus
     * contradictoire possible : « Le déploiement a échoué » au-dessus d'un « 100 % »
     * et d'une barre dont un segment était rouge. Un échec n'est pas un travail
     * achevé.
     */
    FAILED: { start: 0, end: 0 },
    CANCELLED: { start: 0, end: 0 },
};
/**
 * Progression à retenir en entrant dans un état terminal.
 *
 * `DONE` vaut 100 %. Un échec ou une annulation conservent la progression
 * atteinte : c'est jusque-là que le travail est allé, et la barre doit le dire.
 *
 * @param state état terminal visé
 * @param current progression atteinte avant la bascule
 */
export function terminalProgress(state, current) {
    return state === 'DONE' ? 100 : current;
}
/** Libellés affichés à l'utilisateur (§3.3). */
export const STATE_LABELS = {
    QUEUED: "En file d'attente…",
    TRANSFORMING: 'Adaptation du code à nos standards (IA)…',
    AWAITING_REVIEW: 'En attente de votre validation',
    SCAFFOLDING: 'Préparation du projet…',
    BUILDING: "Construction de l'image Docker…",
    PUSHING: 'Envoi vers le registre…',
    DEPLOYING: 'Déploiement sur Cloudron…',
    HEALTHCHECK: "Vérification de l'application…",
    DONE: 'Application en ligne',
    FAILED: 'Le déploiement a échoué',
    CANCELLED: 'Déploiement annulé',
};
/** Indique si l'état est terminal (plus aucune transition possible). */
export function isTerminal(state) {
    return TERMINAL_STATES.includes(state);
}
/**
 * Indique si le déploiement attend une décision de l'utilisateur.
 *
 * Cet état n'est ni actif ni terminal, et cette troisième catégorie doit être
 * traitée comme telle : le job a quitté la file, plus aucun événement n'arrivera,
 * mais le déploiement n'est pas fini pour autant. L'interface doit donc arrêter
 * ses compteurs et cesser d'annoncer « vous pouvez fermer cette page, le
 * déploiement se poursuit » — il ne se poursuivra pas tout seul.
 */
export function isAwaitingUser(state) {
    return state === 'AWAITING_REVIEW';
}
/** Indique si l'état correspond à une étape exécutée par le builder. */
export function isPipelineStep(state) {
    return PIPELINE_STEPS.includes(state);
}
/**
 * Calcule la progression globale en pourcentage.
 *
 * @param state état courant du déploiement
 * @param stepProgress avancement à l'intérieur de l'étape, entre 0 et 1
 *                     (borné automatiquement)
 * @returns pourcentage entre 0 et 100, arrondi à l'entier
 */
export function computeGlobalProgress(state, stepProgress = 0) {
    const range = PROGRESS_RANGES[state];
    // NaN ne porte aucune information : on repart du début de l'étape. Les
    // infinis, eux, sont simplement ramenés dans l'intervalle [0, 1].
    const ratio = Math.min(1, Math.max(0, Number.isNaN(stepProgress) ? 0 : stepProgress));
    return Math.round(range.start + (range.end - range.start) * ratio);
}
//# sourceMappingURL=states.js.map